import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { isScoredType } from 'js/utils/question-set';

const QuestionSelectionContext = createContext({
  selectedIds: new Set(),
  selectedCount: 0,
  totalQuestionCount: 0,
  isAllQuestionsSelected: false,
  isQuestionSelected: () => false,
  toggleQuestion: () => {},
  toggleQuestionSet: () => {},
  selectAllQuestions: () => {},
  getQuestionSetSelectionState: () => ({
    checked: false,
    indeterminate: false,
  }),
  clearSelection: () => {},
});

function questionId(question) {
  return `${question.question_set_key}::${question.key}`;
}

function buildQuestionMap(questionSets) {
  const byId = new Map();
  for (const questionSet of questionSets ?? []) {
    for (const question of questionSet.questions ?? []) {
      byId.set(questionId(question), question);
    }
  }
  return byId;
}

function scorableQuestionIds(questionSet) {
  return (questionSet.questions ?? [])
    .filter((q) => isScoredType(q?.type ?? q?.question_type))
    .map((q) => questionId(q));
}

export function QuestionSelectionProvider({
  questionSets,
  selectionMode = 'multi',
  children,
}) {
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const questionsById = useMemo(
    () => buildQuestionMap(questionSets),
    [questionSets]
  );

  const totalQuestionCount = useMemo(() => {
    let n = 0;
    for (const qs of questionSets ?? []) {
      for (const q of qs.questions ?? []) {
        if (isScoredType(q?.type ?? q?.question_type)) {
          n += 1;
        }
      }
    }
    return n;
  }, [questionSets]);

  const isAllQuestionsSelected =
    totalQuestionCount > 0 && selectedIds.size === totalQuestionCount;

  const isQuestionSelected = useCallback(
    (question) => selectedIds.has(questionId(question)),
    [selectedIds]
  );

  const toggleQuestion = useCallback(
    (question) => {
      if (!isScoredType(question?.type ?? question?.question_type)) {
        return;
      }
      const id = questionId(question);
      setSelectedIds((current) => {
        if (selectionMode === 'single') {
          const next = new Set();
          if (current.has(id)) {
            return next;
          }
          next.add(id);
          return next;
        }
        const next = new Set(current);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    },
    [selectionMode]
  );

  const toggleQuestionSet = useCallback(
    (questionSet) => {
      if (selectionMode === 'single') {
        return;
      }
      const ids = scorableQuestionIds(questionSet);
      if (ids.length === 0) {
        return;
      }
      setSelectedIds((current) => {
        const next = new Set(current);
        const everySelected = ids.every((id) => next.has(id));
        if (everySelected) {
          ids.forEach((id) => next.delete(id));
        } else {
          ids.forEach((id) => next.add(id));
        }
        return next;
      });
    },
    [selectionMode]
  );

  const getQuestionSetSelectionState = useCallback(
    (questionSet) => {
      const ids = scorableQuestionIds(questionSet);
      if (ids.length === 0) {
        return { checked: false, indeterminate: false };
      }
      let selected = 0;
      for (const id of ids) {
        if (selectedIds.has(id)) {
          selected += 1;
        }
      }
      return {
        checked: selected === ids.length,
        indeterminate: selected > 0 && selected < ids.length,
      };
    },
    [selectedIds]
  );

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const selectAllQuestions = useCallback(() => {
    if (selectionMode === 'single') {
      return;
    }
    const next = new Set();
    for (const qs of questionSets ?? []) {
      for (const q of qs.questions ?? []) {
        if (isScoredType(q?.type ?? q?.question_type)) {
          next.add(questionId(q));
        }
      }
    }
    setSelectedIds(next);
  }, [selectionMode, questionSets]);

  const selectedQuestions = useMemo(() => {
    const out = [];
    selectedIds.forEach((id) => {
      const question = questionsById.get(id);
      if (question) {
        out.push(question);
      }
    });
    return out;
  }, [questionsById, selectedIds]);

  const value = useMemo(
    () => ({
      selectedIds,
      selectedCount: selectedIds.size,
      totalQuestionCount,
      isAllQuestionsSelected,
      selectedQuestions,
      isQuestionSelected,
      toggleQuestion,
      toggleQuestionSet,
      selectAllQuestions,
      getQuestionSetSelectionState,
      clearSelection,
    }),
    [
      selectedIds,
      totalQuestionCount,
      isAllQuestionsSelected,
      selectedQuestions,
      isQuestionSelected,
      toggleQuestion,
      toggleQuestionSet,
      selectAllQuestions,
      getQuestionSetSelectionState,
      clearSelection,
    ]
  );

  return (
    <QuestionSelectionContext.Provider value={value}>
      {children}
    </QuestionSelectionContext.Provider>
  );
}

export function useQuestionSelection() {
  return useContext(QuestionSelectionContext);
}
